import { useState, useMemo, useRef, useEffect } from "react";
import "./AllTransaction.css";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";
import axios from "axios";

type FilterPeriod = "semaine" | "mois" | "annee" | "tous";
type TabType = "plateforme" | "colis";

interface TransactionBase {
  id: number | string;
  type: string;
  date: string;
  ref_commande?: string | null;
}

interface PlatformDetail {
  id: number;
  transaction_id: number;
  ref_commande?: string | null;
  scenario_type?: string;
  magasin_vendeur: string;
  magasin_acheteur: string;
  client?: string;
  montant_transaction: string;
  benefice_magasin_originale?: string | null;
  benefice_intermediaire?: string | null;
  benefice_wyn?: string | null;
  total_remise?: string | null;
  commission_wyn_rate?: number;
  has_intermediaire?: boolean;
  intermediaire_name?: string | null;
  benefice_magasin: string | null;
  benefice_commercial: string | null;
  benefice_platform: string;
  type_utilisateur?: string;
  statut?: string;
}

interface ColisDetail {
  id: number;
  transaction_id: number;
  ref_commande?: string | null;
  magasin_vendeur: string;
  magasin_retrait: string;
  client: string;
  frais_livraison: string;
  benefice_magasin: string;
  benefice_magasin_retrait: string;
  benefice_platform: string;
  montant: string;
  adresse: string;
}

interface TransactionData extends TransactionBase {
  type_utilisateur?: string;
  transaction_platform: PlatformDetail | null;
  transaction_colis: ColisDetail | null;
}

interface Popup {
  show: boolean;
  type: "success" | "error" | "warning";
  message: string;
}

function getWeekRange(date: Date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return { start: mon, end: sun };
}

function parseSafeDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  let formatted = dateStr;
  if (formatted.includes(" ") && !formatted.includes("T")) {
    formatted = formatted.replace(" ", "T");
  }
  const d = new Date(formatted);
  if (isNaN(d.getTime())) {
    return new Date();
  }
  return d;
}

function isInPeriod(dateStr: string, period: FilterPeriod): boolean {
  if (period === "tous") return true;
  const date = parseSafeDate(dateStr);
  const now = new Date();
  if (period === "semaine") {
    const { start, end } = getWeekRange(now);
    return date >= start && date <= end;
  } else if (period === "mois") {
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  } else {
    return date.getFullYear() === now.getFullYear();
  }
}

function fmt(n: number) {
  return n.toLocaleString("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " MAD";
}

function fmtOrNa(val: string | null | undefined): string {
  if (val === null || val === undefined) return "N/A";
  const n = parseFloat(val);
  if (isNaN(n)) return "N/A";
  return fmt(n);
}

function safeParseFloat(val: string | null | undefined): number {
  if (val === null || val === undefined) return 0;
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function getUserTypeBadge(type?: string) {
  if (!type) return null;
  const t = type.toLowerCase();
  if (t === "visiteur") return { label: "Visiteur", cls: "td-badge-visiteur" };
  if (t === "magasin") return { label: "Magasin", cls: "td-badge-magasin" };
  if (t === "commercial") return { label: "Commercial", cls: "td-badge-commercial" };
  if (t === "client") return { label: "Client", cls: "td-badge-client" };
  return { label: type, cls: "td-badge-default" };
}

function getScenarioBadge(scenario?: string) {
  if (!scenario) return { label: "Direct Plateforme", cls: "td-badge-direct" };
  switch (scenario) {
    case "VISITOR_PURCHASE":
      return { label: "Achat Visiteur Direct", cls: "td-badge-visiteur" };
    case "INTERMEDIARY_STORE":
      return { label: "Vente via Intermédiaire", cls: "td-badge-inter" };
    case "STORE_TO_STORE":
      return { label: "Magasin à Magasin", cls: "td-badge-magasin" };
    case "CLIENT_PURCHASE":
      return { label: "Achat Client", cls: "td-badge-client" };
    case "COMMERCIAL_PURCHASE":
      return { label: "Achat Commercial", cls: "td-badge-commercial" };
    default:
      return { label: "Direct Plateforme", cls: "td-badge-direct" };
  }
}

function fmtDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("fr-MA", { day: "2-digit", month: "short", year: "numeric" });
}

/** Styled monospace chip for RefCommande codes with copy feature */
function RefBadge({ code }: { code?: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!code) return <span style={{ color: "#94a3b8", fontSize: 11 }}>—</span>;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <span
      className="td-ref-badge"
      title={`Cliquer pour copier: ${code}`}
      onClick={handleCopy}
    >
      <i className={copied ? "bi bi-check2 text-success" : "bi bi-receipt"} style={{ marginRight: 4, fontSize: 11 }} />
      <span>{code}</span>
      <span className="td-ref-copy-hint">{copied ? "Copié!" : "Copier"}</span>
    </span>
  );
}

export default function AllTransaction() {
  const [tab, setTab] = useState<TabType>("plateforme");
  const [period, setPeriod] = useState<FilterPeriod>("tous");
  const printRef = useRef<HTMLDivElement>(null);
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<Popup>({ show: false, type: "success", message: "" });
  const [confirmDelete, setConfirmDelete] = useState<number | string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<number | string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionData | null>(null);

  // New filters & loader
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [fetching, setFetching] = useState(false);

  // View Mode: "table" on desktop, "cards" on mobile/tablet or when toggled
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 860) {
      setViewMode("cards");
    }
  }, []);

  // Pagination
  const PAGE_SIZE = 7;
  const [platformPage, setPlatformPage] = useState(1);
  const [colisPage, setColisPage] = useState(1);

  // Platform form
  const [platformForm, setPlatformForm] = useState({
    magasin_vendeur: "",
    magasin_acheteur: "",
    montant_transaction: "",
    benefice_magasin: "",
    benefice_commercial: "",
    benefice_platform: "",
  });

  // Colis form
  const [colisForm, setColisForm] = useState({
    magasin_vendeur: "",
    magasin_retrait: "",
    client: "",
    frais_livraison: "",
    benefice_magasin: "",
    benefice_magasin_retrait: "",
    benefice_platform: "",
    montant: "",
    adresse: "",
  });

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Reset pages when filters change
  useEffect(() => { setPlatformPage(1); }, [searchTerm, typeFilter, period, tab]);
  useEffect(() => { setColisPage(1); }, [searchTerm, period, tab]);

  useEffect(() => {
    if (popup.show) {
      const timer = setTimeout(() => setPopup((p) => ({ ...p, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [popup.show]);

  const showPopup = (type: Popup["type"], message: string) => {
    setPopup({ show: true, type, message });
  };

  const fetchTransactions = async () => {
    setFetching(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_PHP_APP_URL}/admin/transactions/commandes`
      );
      setTransactions(data);
    } catch (error: any) {
      console.error("Erreur lors du chargement des transactions:", error?.response?.data || error?.message || error);
      showPopup("error", "Erreur lors du chargement des transactions ❌");
    } finally {
      setFetching(false);
    }
  };

  const platformTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (t.type !== "platform" || !t.transaction_platform) return false;
      if (!isInPeriod(t.date, period)) return false;

      // Type Filter
      const userType = (t.type_utilisateur || t.transaction_platform?.type_utilisateur || "").toLowerCase();
      if (typeFilter !== "all" && userType !== typeFilter) return false;

      // Search Filter — includes ref_commande
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const vendor  = (t.transaction_platform.magasin_vendeur || "").toLowerCase();
        const buyer   = (t.transaction_platform.magasin_acheteur || "").toLowerCase();
        const client  = (t.transaction_platform.client || "").toLowerCase();
        const ref     = (t.ref_commande || t.transaction_platform.ref_commande || "").toLowerCase();
        if (!vendor.includes(s) && !buyer.includes(s) && !client.includes(s) && !ref.includes(s)) return false;
      }

      return true;
    });
  }, [transactions, period, searchTerm, typeFilter]);

  const colisTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (t.type !== "colis" || !t.transaction_colis) return false;
      if (!isInPeriod(t.date, period)) return false;

      // Search Filter — includes ref_commande
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const vendor  = (t.transaction_colis.magasin_vendeur || "").toLowerCase();
        const buyer   = (t.transaction_colis.magasin_retrait || "").toLowerCase();
        const client  = (t.transaction_colis.client || "").toLowerCase();
        const ref     = (t.ref_commande || t.transaction_colis.ref_commande || "").toLowerCase();
        if (!vendor.includes(s) && !buyer.includes(s) && !client.includes(s) && !ref.includes(s)) return false;
      }

      return true;
    });
  }, [transactions, period, searchTerm]);

  const platformStats = useMemo(() => ({
    totalMontant:        platformTransactions.reduce((s, t) => s + safeParseFloat(t.transaction_platform?.montant_transaction), 0),
    totalMagasinOrig:   platformTransactions.reduce((s, t) => s + safeParseFloat(t.transaction_platform?.benefice_magasin_originale ?? t.transaction_platform?.benefice_magasin), 0),
    totalIntermediaire: platformTransactions.reduce((s, t) => s + safeParseFloat(t.transaction_platform?.benefice_intermediaire), 0),
    totalWyn:           platformTransactions.reduce((s, t) => s + safeParseFloat(t.transaction_platform?.benefice_wyn ?? t.transaction_platform?.benefice_platform), 0),
    totalRemise:        platformTransactions.reduce((s, t) => s + safeParseFloat(t.transaction_platform?.total_remise), 0),
    totalTransactions: platformTransactions.length,
  }), [platformTransactions]);

  const colisStats = useMemo(() => ({
    totalFrais: colisTransactions.reduce((s, t) => s + parseFloat(t.transaction_colis?.frais_livraison || "0"), 0),
    totalMontant: colisTransactions.reduce((s, t) => s + parseFloat(t.transaction_colis?.montant || "0"), 0),
    totalColis: colisTransactions.length,
  }), [colisTransactions]);

  const openAdd = () => {
    if (tab === "plateforme") {
      setPlatformForm({ magasin_vendeur: "", magasin_acheteur: "", montant_transaction: "", benefice_magasin: "", benefice_commercial: "", benefice_platform: "" });
    } else {
      setColisForm({ magasin_vendeur: "", magasin_retrait: "", client: "", frais_livraison: "", benefice_magasin: "", benefice_magasin_retrait: "", benefice_platform: "", montant: "", adresse: "" });
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (tab === "plateforme") {
        if (!platformForm.magasin_vendeur.trim() || !platformForm.magasin_acheteur.trim() || !platformForm.montant_transaction.trim()) {
          showPopup("warning", "Veuillez remplir tous les champs obligatoires");
          setLoading(false);
          return;
        }
        await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/admin/transaction/platform/create`,
          platformForm
        );
        showPopup("success", "Transaction plateforme ajoutée avec succès ✅");
      } else {
        if (!colisForm.client.trim()) {
          showPopup("warning", "Veuillez remplir tous les champs obligatoires");
          setLoading(false);
          return;
        }
        await axios.post(
          `${process.env.REACT_APP_PHP_APP_URL}/admin/transaction/colis/create`,
          colisForm
        );
        showPopup("success", "Transaction colis ajoutée avec succès ✅");
      }
      await fetchTransactions();
      setShowModal(false);
    } catch (error) {
      showPopup("error", "Erreur lors de l'ajout de la transaction ❌");
    }
    setLoading(false);
  };

  const handleDelete = async (id: number | string) => {
    setDeleteLoading(id);
    try {
      await axios.delete(
        `${process.env.REACT_APP_PHP_APP_URL}/admin/transaction/${id}/delete`
      );
      await fetchTransactions();
      showPopup("success", "Transaction supprimée avec succès ✅");
    } catch (error) {
      showPopup("error", "Erreur lors de la suppression ❌");
    }
    setDeleteLoading(null);
    setConfirmDelete(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const periodLabel: Record<FilterPeriod, string> = {
    tous: "Toutes les transactions",
    semaine: "Cette semaine",
    mois: "Ce mois",
    annee: "Cette année",
  };

  return (
    <>
    <NewSideBarAdmin/>

      {/* ── Toast Popup ── */}
      <div className={`td-toast ${popup.show ? "td-toast--visible" : ""} td-toast--${popup.type}`}>
        <div className="td-toast-icon">
          <i className={`bi ${
            popup.type === "success" ? "bi-check-circle-fill" :
            popup.type === "error" ? "bi-x-circle-fill" :
            "bi-exclamation-triangle-fill"
          }`} />
        </div>
        <span className="td-toast-msg">{popup.message}</span>
        <button className="td-toast-close" onClick={() => setPopup((p) => ({ ...p, show: false }))}>
          <i className="bi bi-x" />
        </button>
      </div>

      {/* ── Confirm Delete Modal ── */}
      {confirmDelete !== null && (
        <>
          <div className="td-overlay" onClick={() => setConfirmDelete(null)} />
          <div className="td-modal td-modal--confirm">
            <div className="td-confirm-body">
              <div className="td-confirm-icon">
                <i className="bi bi-exclamation-triangle-fill" />
              </div>
              <h3>Supprimer cette transaction ?</h3>
              <p>Cette action est irréversible.</p>
              <div className="td-confirm-actions">
                <button className="td-btn-cancel" onClick={() => setConfirmDelete(null)}>Annuler</button>
                <button className="td-btn-delete" onClick={() => handleDelete(confirmDelete)} disabled={deleteLoading === confirmDelete}>
                  {deleteLoading === confirmDelete ? (
                    <><span className="td-spinner" /> Suppression...</>
                  ) : (
                    <><i className="bi bi-trash3" /> Supprimer</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    <div className="td-root" ref={printRef}>

      {/* Header */}
      <header className="td-header no-print">
        <div className="td-header-left">
          <div className="td-logo">
            <span className="td-logo-icon">◈</span>
            <span className="td-logo-text">Transaction</span>
          </div>
          <p className="td-subtitle">Tableau de bord des transactions</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="td-print-btn" onClick={handlePrint}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Imprimer
          </button>
        </div>
      </header>

      {/* Print Header */}
      <div className="print-only print-header">
        <div className="print-title">◈ Transaction — Rapport de Transactions</div>
        <div className="print-meta">
          Période : {periodLabel[period]} · Type : {tab === "plateforme" ? "Transactions Plateforme" : "Transactions Colis"} · Généré le {new Date().toLocaleDateString("fr-MA", { day: "2-digit", month: "long", year: "numeric" })}
        </div>
      </div>

      {/* Filters */}
      <div className="td-filters no-print">
        <div className="td-filters-left">
          <div className="td-tabs">
            <button className={`td-tab ${tab === "plateforme" ? "active" : ""}`} onClick={() => setTab("plateforme")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
              Transactions Plateforme
            </button>
            <button className={`td-tab ${tab === "colis" ? "active" : ""}`} onClick={() => setTab("colis")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              Transactions Colis
            </button>
          </div>

          <div className="td-search-wrap">
            <i className="bi bi-search" />
            <input 
              type="text" 
              placeholder="Rechercher par acheteur, vendeur, client ou code commande…" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }} onClick={() => setSearchTerm("")}>
                <i className="bi bi-x-circle-fill" />
              </button>
            )}
          </div>

          {tab === "plateforme" && (
            <div className="td-type-filter">
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="all">Tous les types</option>
                <option value="visiteur">Visiteur</option>
                <option value="magasin">Magasin</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
          )}
        </div>

        <div className="td-period-pills">
          {(["tous", "semaine", "mois", "annee"] as FilterPeriod[]).map((p) => (
            <button key={p} className={`td-pill ${period === p ? "active" : ""}`} onClick={() => setPeriod(p)}>
              {p === "tous" ? "Tout" : p === "semaine" ? "Semaine" : p === "mois" ? "Mois" : "Année"}
            </button>
          ))}
        </div>
      </div>

      {/* Print filters */}
      <div className="print-only print-filter-bar">
        <span>Type : <strong>{tab === "plateforme" ? "Plateforme" : "Colis"}</strong></span>
        <span>Période : <strong>{periodLabel[period]}</strong></span>
      </div>

      {/* Stats Cards */}
      {tab === "plateforme" ? (
        <div className="td-stats no-print">
          <div className="td-stat-card td-stat-total">
            <span className="td-stat-label">Montant Total</span>
            <span className="td-stat-value">{fmt(platformStats.totalMontant)}</span>
            <span className="td-stat-sub">{platformStats.totalTransactions} transaction{platformStats.totalTransactions !== 1 ? "s" : ""}</span>
          </div>
          <div className="td-stat-card td-stat-magasin">
            <span className="td-stat-label">Bénéfice Magasin Origine</span>
            <span className="td-stat-value">{fmt(platformStats.totalMagasinOrig)}</span>
            <span className="td-stat-sub">Prix net vendeurs propriétaires</span>
          </div>
          <div className="td-stat-card td-stat-intermediaire">
            <span className="td-stat-label">Bénéfice Intermédiaire</span>
            <span className="td-stat-value">{fmt(platformStats.totalIntermediaire)}</span>
            <span className="td-stat-sub">Magasins revendeurs</span>
          </div>
          <div className="td-stat-card td-stat-platform">
            <span className="td-stat-label">Bénéfice WYN</span>
            <span className="td-stat-value">{fmt(platformStats.totalWyn)}</span>
            <span className="td-stat-sub">Commission plateforme</span>
          </div>
        </div>
      ) : (
        <div className="td-stats no-print">
          <div className="td-stat-card td-stat-total">
            <span className="td-stat-label">Montant Total Colis</span>
            <span className="td-stat-value">{fmt(colisStats.totalMontant)}</span>
            <span className="td-stat-sub">{colisStats.totalColis} colis</span>
          </div>
          <div className="td-stat-card td-stat-magasin">
            <span className="td-stat-label">Total Frais Livraison</span>
            <span className="td-stat-value">{fmt(colisStats.totalFrais)}</span>
            <span className="td-stat-sub">Tous les colis</span>
          </div>
          <div className="td-stat-card td-stat-platform">
            <span className="td-stat-label">Nombre de Colis</span>
            <span className="td-stat-value">{colisStats.totalColis}</span>
            <span className="td-stat-sub">Colis enregistrés</span>
          </div>
        </div>
      )}

      {/* Content */}
      {tab === "plateforme" ? (
        <>
          {/* Platform Table */}
          <div className="td-table-wrap">
            {fetching && (
              <div className="td-table-loader">
                <span className="td-spinner td-spinner--lg" />
                <p>Chargement des transactions...</p>
              </div>
            )}
            <div className="td-table-header">
              <div className="td-table-header-left">
                <span className="td-table-title">Détail des transactions</span>
                <span className="td-table-period-tag">{periodLabel[period]}</span>
                <span className="td-count">{platformTransactions.length} entrées</span>
              </div>
              <div className="td-view-switch">
                <button
                  type="button"
                  className={`td-view-btn ${viewMode === "table" ? "active" : ""}`}
                  onClick={() => setViewMode("table")}
                  title="Affichage Tableau"
                >
                  <i className="bi bi-table" />
                  <span className="td-view-label">Tableau</span>
                </button>
                <button
                  type="button"
                  className={`td-view-btn ${viewMode === "cards" ? "active" : ""}`}
                  onClick={() => setViewMode("cards")}
                  title="Affichage Cartes (Mobile)"
                >
                  <i className="bi bi-grid-fill" />
                  <span className="td-view-label">Cartes</span>
                </button>
              </div>
            </div>

            {platformTransactions.length === 0 ? (
              <div className="td-empty">Aucune transaction pour cette période</div>
            ) : (
              <>
              {viewMode === "table" ? (
                <div className="td-table-scroll">
                  <div className="td-mobile-scroll-hint">
                    <i className="bi bi-arrows-expand" /> Glissez horizontalement pour voir toutes les colonnes
                  </div>
                  <table className="td-table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 45 }}>#</th>
                        <th style={{ minWidth: 160 }}><i className="bi bi-upc-scan" style={{ marginRight: 6, color: "#6366f1" }} />Code Commande</th>
                        <th style={{ minWidth: 155 }}><i className="bi bi-tag" style={{ marginRight: 6, color: "#64748b" }} />Scénario / Type</th>
                        <th style={{ minWidth: 145 }}><i className="bi bi-shop" style={{ marginRight: 6, color: "#059669" }} />Magasin Origine</th>
                        <th style={{ minWidth: 165 }}><i className="bi bi-person-badge" style={{ marginRight: 6, color: "#0284c7" }} />Magasin Acheteur</th>
                        <th className="num" style={{ minWidth: 125 }}><i className="bi bi-cash-stack" style={{ marginRight: 6, color: "#0f172a" }} />Montant Total</th>
                        <th className="num" style={{ minWidth: 130 }}><i className="bi bi-wallet2" style={{ marginRight: 6, color: "#059669" }} />Bénéf. Magasin</th>
                        <th className="num" style={{ minWidth: 125 }}><i className="bi bi-arrow-left-right" style={{ marginRight: 6, color: "#d97706" }} />Bénéf. Inter.</th>
                        <th className="num" style={{ minWidth: 125 }}><i className="bi bi-gem" style={{ marginRight: 6, color: "#4f46e5" }} />Bénéfice WYN</th>
                        <th style={{ minWidth: 105 }}><i className="bi bi-calendar3" style={{ marginRight: 6, color: "#64748b" }} />Date</th>
                        <th style={{ minWidth: 70, textAlign: "center" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {platformTransactions.slice((platformPage - 1) * PAGE_SIZE, platformPage * PAGE_SIZE).map((t, i) => {
                        const badge = getUserTypeBadge(t.type_utilisateur || t.transaction_platform?.type_utilisateur);
                        const scenarioBadge = getScenarioBadge(t.transaction_platform?.scenario_type);
                        const p = t.transaction_platform;
                        const beneficeMagOrig   = safeParseFloat(p?.benefice_magasin_originale ?? p?.benefice_magasin);
                        const beneficeInter     = safeParseFloat(p?.benefice_intermediaire);
                        const beneficeWyn       = safeParseFloat(p?.benefice_wyn ?? p?.benefice_platform);
                        const hasInter          = p?.has_intermediaire || beneficeInter > 0;
                        const globalIndex = (platformPage - 1) * PAGE_SIZE + i + 1;
                        const refCode     = t.ref_commande || p?.ref_commande;
                        return (
                        <tr key={String(t.id)} onClick={() => setSelectedTransaction(t)} style={{ cursor: "pointer" }} title="Cliquer pour voir le détail de la transaction">
                          <td><span className="td-index-badge">{globalIndex}</span></td>
                          <td><RefBadge code={refCode} /></td>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              <span className={`td-type-badge ${scenarioBadge.cls}`}>{scenarioBadge.label}</span>
                              {badge && <span style={{ fontSize: "10px", opacity: 0.85 }}>({badge.label})</span>}
                            </div>
                          </td>
                          <td>
                            <div className="td-store-cell">
                              <i className="bi bi-shop td-store-icon" />
                              <span className="td-store">{p?.magasin_vendeur}</span>
                            </div>
                          </td>
                          <td>
                            <div className="td-store-cell">
                              <i className="bi bi-person-badge td-store-icon" />
                              <span className="td-store">{p?.magasin_acheteur}</span>
                              {hasInter && <span className="td-inter-badge" style={{ marginLeft: "6px" }}>Inter.</span>}
                            </div>
                          </td>
                          <td className="num bold td-total-cell">{fmt(safeParseFloat(p?.montant_transaction))}</td>
                          <td className="num td-magasin-val"><span className="td-val-pill td-val-pill--green">{fmt(beneficeMagOrig)}</span></td>
                          <td className={`num ${hasInter ? "td-intermediaire-val" : "td-na-val"}`}>
                            {hasInter ? <span className="td-val-pill td-val-pill--amber">{fmt(beneficeInter)}</span> : "—"}
                          </td>
                          <td className="num td-platform-val"><span className="td-val-pill td-val-pill--indigo">{fmt(beneficeWyn)}</span></td>
                          <td style={{ whiteSpace: "nowrap" }}><span className="td-date-cell">{fmtDate(t.date)}</span></td>
                          <td style={{ textAlign: "center" }}>
                            <button className="td-detail-btn" onClick={(e) => { e.stopPropagation(); setSelectedTransaction(t); }} title="Voir détails">
                              <i className="bi bi-eye-fill" />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} className="bold">Total général ({platformTransactions.length} transactions)</td>
                        <td className="num bold">{fmt(platformStats.totalMontant)}</td>
                        <td className="num bold td-magasin-val">{fmt(platformStats.totalMagasinOrig)}</td>
                        <td className="num bold td-intermediaire-val">{fmt(platformStats.totalIntermediaire)}</td>
                        <td className="num bold td-platform-val">{fmt(platformStats.totalWyn)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                /* Card View for Mobile/Tablet */
                <div className="td-cards-grid">
                  {platformTransactions.slice((platformPage - 1) * PAGE_SIZE, platformPage * PAGE_SIZE).map((t, i) => {
                    const badge = getUserTypeBadge(t.type_utilisateur || t.transaction_platform?.type_utilisateur);
                    const scenarioBadge = getScenarioBadge(t.transaction_platform?.scenario_type);
                    const p = t.transaction_platform;
                    const beneficeMagOrig   = safeParseFloat(p?.benefice_magasin_originale ?? p?.benefice_magasin);
                    const beneficeInter     = safeParseFloat(p?.benefice_intermediaire);
                    const beneficeWyn       = safeParseFloat(p?.benefice_wyn ?? p?.benefice_platform);
                    const hasInter          = p?.has_intermediaire || beneficeInter > 0;
                    const globalIndex = (platformPage - 1) * PAGE_SIZE + i + 1;
                    const refCode     = t.ref_commande || p?.ref_commande;

                    return (
                      <div key={String(t.id)} className="td-card" onClick={() => setSelectedTransaction(t)}>
                        <div className="td-card-header">
                          <div className="td-card-id-group">
                            <span className="td-card-index">#{globalIndex}</span>
                            <RefBadge code={refCode} />
                          </div>
                          <div className="td-card-badges">
                            <span className={`td-type-badge ${scenarioBadge.cls}`}>{scenarioBadge.label}</span>
                            {badge && <span className="td-badge-sub">({badge.label})</span>}
                          </div>
                        </div>

                        <div className="td-card-route">
                          <div className="td-card-store">
                            <span className="td-card-store-label">Vendeur (Origine)</span>
                            <span className="td-card-store-name"><i className="bi bi-shop" /> {p?.magasin_vendeur || "—"}</span>
                          </div>
                          <div className="td-card-arrow">
                            <i className="bi bi-arrow-right" />
                          </div>
                          <div className="td-card-store">
                            <span className="td-card-store-label">Acheteur / Client</span>
                            <span className="td-card-store-name">
                              <i className="bi bi-person-badge" /> {p?.magasin_acheteur || "—"}
                              {hasInter && <span className="td-inter-badge" style={{ marginLeft: 6 }}>Inter.</span>}
                            </span>
                          </div>
                        </div>

                        <div className="td-card-metrics">
                          <div className="td-card-metric td-card-metric--total">
                            <span className="td-metric-label">Montant Total</span>
                            <span className="td-metric-val">{fmt(safeParseFloat(p?.montant_transaction))}</span>
                          </div>
                          <div className="td-card-metric td-card-metric--mag">
                            <span className="td-metric-label">Bénéf. Magasin</span>
                            <span className="td-metric-val">{fmt(beneficeMagOrig)}</span>
                          </div>
                          <div className="td-card-metric td-card-metric--inter">
                            <span className="td-metric-label">Bénéf. Inter.</span>
                            <span className="td-metric-val">{hasInter ? fmt(beneficeInter) : "—"}</span>
                          </div>
                          <div className="td-card-metric td-card-metric--wyn">
                            <span className="td-metric-label">Bénéf. WYN</span>
                            <span className="td-metric-val">{fmt(beneficeWyn)}</span>
                          </div>
                        </div>

                        <div className="td-card-footer">
                          <span className="td-card-date"><i className="bi bi-calendar3" /> {fmtDate(t.date)}</span>
                          <button className="td-card-action-btn" onClick={(e) => { e.stopPropagation(); setSelectedTransaction(t); }}>
                            <i className="bi bi-eye-fill" /> Détails
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination Platform */}
              {Math.ceil(platformTransactions.length / PAGE_SIZE) > 1 && (
                <div className="td-pagination">
                  <button
                    className="td-page-btn"
                    onClick={() => setPlatformPage(p => Math.max(1, p - 1))}
                    disabled={platformPage === 1}
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                  <div className="td-page-numbers">
                    {Array.from({ length: Math.ceil(platformTransactions.length / PAGE_SIZE) }, (_, idx) => idx + 1).map(num => (
                      <button
                        key={num}
                        className={`td-page-num ${platformPage === num ? "active" : ""}`}
                        onClick={() => setPlatformPage(num)}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  <button
                    className="td-page-btn"
                    onClick={() => setPlatformPage(p => Math.min(Math.ceil(platformTransactions.length / PAGE_SIZE), p + 1))}
                    disabled={platformPage === Math.ceil(platformTransactions.length / PAGE_SIZE)}
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                  <span className="td-page-info">
                    Page {platformPage} / {Math.ceil(platformTransactions.length / PAGE_SIZE)}
                    &nbsp;·&nbsp;
                    {Math.min(platformPage * PAGE_SIZE, platformTransactions.length)} sur {platformTransactions.length}
                  </span>
                </div>
              )}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Colis Table */}
          <div className="td-table-wrap">
            {fetching && (
              <div className="td-table-loader">
                <span className="td-spinner td-spinner--lg" />
                <p>Chargement des transactions...</p>
              </div>
            )}
            <div className="td-table-header">
              <div className="td-table-header-left">
                <span className="td-table-title">Suivi des colis</span>
                <span className="td-table-period-tag">{periodLabel[period]}</span>
                <span className="td-count">{colisTransactions.length} colis</span>
              </div>
              <div className="td-view-switch">
                <button
                  type="button"
                  className={`td-view-btn ${viewMode === "table" ? "active" : ""}`}
                  onClick={() => setViewMode("table")}
                  title="Affichage Tableau"
                >
                  <i className="bi bi-table" />
                  <span className="td-view-label">Tableau</span>
                </button>
                <button
                  type="button"
                  className={`td-view-btn ${viewMode === "cards" ? "active" : ""}`}
                  onClick={() => setViewMode("cards")}
                  title="Affichage Cartes (Mobile)"
                >
                  <i className="bi bi-grid-fill" />
                  <span className="td-view-label">Cartes</span>
                </button>
              </div>
            </div>

            {colisTransactions.length === 0 ? (
              <div className="td-empty">Aucun colis pour cette période</div>
            ) : (
              <>
              {viewMode === "table" ? (
                <div className="td-table-scroll">
                  <div className="td-mobile-scroll-hint">
                    <i className="bi bi-arrows-expand" /> Glissez horizontalement pour voir toutes les colonnes
                  </div>
                  <table className="td-table">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 75 }}>ID Colis</th>
                        <th style={{ minWidth: 160 }}><i className="bi bi-upc-scan" style={{ marginRight: 6, color: "#6366f1" }} />Code Commande</th>
                        <th style={{ minWidth: 145 }}><i className="bi bi-person" style={{ marginRight: 6, color: "#0284c7" }} />Client</th>
                        <th style={{ minWidth: 135 }}><i className="bi bi-shop" style={{ marginRight: 6, color: "#059669" }} />Magasin Vendeur</th>
                        <th className="num" style={{ minWidth: 115 }}><i className="bi bi-cash" style={{ marginRight: 6, color: "#0f172a" }} />Montant</th>
                        <th style={{ minWidth: 175 }}><i className="bi bi-geo-alt" style={{ marginRight: 6, color: "#64748b" }} />Adresse</th>
                        <th style={{ minWidth: 155 }}><i className="bi bi-truck" style={{ marginRight: 6, color: "#d97706" }} />Soc. Livraison</th>
                        <th className="num" style={{ minWidth: 130 }}><i className="bi bi-box" style={{ marginRight: 6, color: "#059669" }} />Frais Livraison</th>
                        <th style={{ minWidth: 105 }}><i className="bi bi-calendar3" style={{ marginRight: 6, color: "#64748b" }} />Date</th>
                        <th style={{ minWidth: 75, textAlign: "center" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {colisTransactions.slice((colisPage - 1) * PAGE_SIZE, colisPage * PAGE_SIZE).map((c) => {
                        const refCode = c.ref_commande || c.transaction_colis?.ref_commande;
                        return (
                        <tr key={String(c.id)} onClick={() => setSelectedTransaction(c)} style={{ cursor: "pointer" }} title="Cliquer pour voir le détail">
                          <td><span className="td-index-badge">{c.transaction_colis?.id || c.id}</span></td>
                          <td><RefBadge code={refCode} /></td>
                          <td>
                            <div className="td-store-cell">
                              <i className="bi bi-person td-store-icon" />
                              <span className="td-store">{c.transaction_colis?.client}</span>
                            </div>
                          </td>
                          <td>
                            <div className="td-store-cell">
                              <i className="bi bi-shop td-store-icon" />
                              <span className="td-store">{c.transaction_colis?.magasin_vendeur || "—"}</span>
                            </div>
                          </td>
                          <td className="num bold td-total-cell">{fmt(parseFloat(c.transaction_colis?.montant || "0"))}</td>
                          <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.transaction_colis?.adresse}>
                            <span className="td-address-cell"><i className="bi bi-geo-alt" style={{ marginRight: 4, color: "#94a3b8" }} />{c.transaction_colis?.adresse}</span>
                          </td>
                          <td>
                            <div className="td-store-cell">
                              <i className="bi bi-truck td-store-icon" />
                              <span className="td-store">{c.transaction_colis?.magasin_retrait}</span>
                            </div>
                          </td>
                          <td className="num bold td-magasin-val"><span className="td-val-pill td-val-pill--green">{fmt(parseFloat(c.transaction_colis?.frais_livraison || "0"))}</span></td>
                          <td style={{ whiteSpace: "nowrap" }}><span className="td-date-cell">{fmtDate(c.date)}</span></td>
                          <td style={{ textAlign: "center" }}>
                            <div style={{ display: "inline-flex", gap: "6px" }}>
                              <button className="td-detail-btn" onClick={(e) => { e.stopPropagation(); setSelectedTransaction(c); }} title="Voir détails">
                                <i className="bi bi-eye-fill" />
                              </button>
                              <button className="td-action-delete" onClick={(e) => { e.stopPropagation(); setConfirmDelete(c.id as number); }} title="Supprimer">
                                <i className="bi bi-trash3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} className="bold">Total général ({colisTransactions.length} colis)</td>
                        <td className="num bold">{fmt(colisStats.totalMontant)}</td>
                        <td colSpan={2}></td>
                        <td className="num bold td-magasin-val">{fmt(colisStats.totalFrais)}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                /* Card View for Colis */
                <div className="td-cards-grid">
                  {colisTransactions.slice((colisPage - 1) * PAGE_SIZE, colisPage * PAGE_SIZE).map((c) => {
                    const refCode = c.ref_commande || c.transaction_colis?.ref_commande;
                    return (
                      <div key={String(c.id)} className="td-card" onClick={() => setSelectedTransaction(c)}>
                        <div className="td-card-header">
                          <div className="td-card-id-group">
                            <span className="td-card-index">Colis #{c.transaction_colis?.id || c.id}</span>
                            <RefBadge code={refCode} />
                          </div>
                          <span className="td-card-date"><i className="bi bi-calendar3" /> {fmtDate(c.date)}</span>
                        </div>

                        <div className="td-card-route">
                          <div className="td-card-store">
                            <span className="td-card-store-label">Magasin Vendeur</span>
                            <span className="td-card-store-name"><i className="bi bi-shop" /> {c.transaction_colis?.magasin_vendeur || "—"}</span>
                          </div>
                          <div className="td-card-arrow">
                            <i className="bi bi-truck" />
                          </div>
                          <div className="td-card-store">
                            <span className="td-card-store-label">Client</span>
                            <span className="td-card-store-name"><i className="bi bi-person" /> {c.transaction_colis?.client || "—"}</span>
                          </div>
                        </div>

                        {c.transaction_colis?.adresse && (
                          <div className="td-card-address">
                            <i className="bi bi-geo-alt-fill" style={{ color: "#ef4444", marginRight: 4 }} />
                            <span>{c.transaction_colis.adresse}</span>
                          </div>
                        )}

                        <div className="td-card-metrics">
                          <div className="td-card-metric td-card-metric--total">
                            <span className="td-metric-label">Montant Colis</span>
                            <span className="td-metric-val">{fmt(parseFloat(c.transaction_colis?.montant || "0"))}</span>
                          </div>
                          <div className="td-card-metric td-card-metric--mag">
                            <span className="td-metric-label">Frais Livraison</span>
                            <span className="td-metric-val">{fmt(parseFloat(c.transaction_colis?.frais_livraison || "0"))}</span>
                          </div>
                        </div>

                        <div className="td-card-footer">
                          <span className="td-card-retrait">
                            <i className="bi bi-box-seam" /> {c.transaction_colis?.magasin_retrait || "Livraison"}
                          </span>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button className="td-card-action-btn" onClick={(e) => { e.stopPropagation(); setSelectedTransaction(c); }}>
                              <i className="bi bi-eye-fill" /> Détails
                            </button>
                            <button className="td-action-delete" onClick={(e) => { e.stopPropagation(); setConfirmDelete(c.id as number); }} title="Supprimer">
                              <i className="bi bi-trash3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination Colis */}
              {Math.ceil(colisTransactions.length / PAGE_SIZE) > 1 && (
                <div className="td-pagination">
                  <button
                    className="td-page-btn"
                    onClick={() => setColisPage(p => Math.max(1, p - 1))}
                    disabled={colisPage === 1}
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                  <div className="td-page-numbers">
                    {Array.from({ length: Math.ceil(colisTransactions.length / PAGE_SIZE) }, (_, idx) => idx + 1).map(num => (
                      <button
                        key={num}
                        className={`td-page-num ${colisPage === num ? "active" : ""}`}
                        onClick={() => setColisPage(num)}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  <button
                    className="td-page-btn"
                    onClick={() => setColisPage(p => Math.min(Math.ceil(colisTransactions.length / PAGE_SIZE), p + 1))}
                    disabled={colisPage === Math.ceil(colisTransactions.length / PAGE_SIZE)}
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                  <span className="td-page-info">
                    Page {colisPage} / {Math.ceil(colisTransactions.length / PAGE_SIZE)}
                    &nbsp;·&nbsp;
                    {Math.min(colisPage * PAGE_SIZE, colisTransactions.length)} sur {colisTransactions.length}
                  </span>
                </div>
              )}
              </>
            )}
          </div>
        </>
      )}

      {/* Footer */}
      <footer className="td-footer no-print">
        <span>Transaction · Système de gestion des transactions</span>
        <span>{new Date().toLocaleDateString("fr-MA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
      </footer>

      <div className="print-only print-footer">
        <span>Transaction — Document confidentiel</span>
        <span>Page 1 / 1</span>
      </div>

      {/* ── Add Transaction Modal ── */}
      {showModal && (
        <>
          <div className="td-overlay" onClick={() => !loading && setShowModal(false)} />
          <div className="td-modal">
            {loading && (
              <div className="td-modal-loader">
                <span className="td-spinner td-spinner--lg" />
                <p>Ajout en cours...</p>
              </div>
            )}
            <div className="td-modal-header">
              <h3>{tab === "plateforme" ? "Nouvelle Transaction Plateforme" : "Nouvelle Transaction Colis"}</h3>
              <button className="td-modal-close" onClick={() => !loading && setShowModal(false)}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="td-modal-body">
              {tab === "plateforme" ? (
                <>
                  <div className="td-field">
                    <label><i className="bi bi-shop" /> Magasin Vendeur</label>
                    <input type="text" placeholder="Ex: Magasin Atlas" value={platformForm.magasin_vendeur}
                      onChange={(e) => setPlatformForm({ ...platformForm, magasin_vendeur: e.target.value })} disabled={loading} />
                  </div>
                  <div className="td-field">
                    <label><i className="bi bi-shop-window" /> Magasin Acheteur</label>
                    <input type="text" placeholder="Ex: Magasin Rif" value={platformForm.magasin_acheteur}
                      onChange={(e) => setPlatformForm({ ...platformForm, magasin_acheteur: e.target.value })} disabled={loading} />
                  </div>
                  <div className="td-field">
                    <label><i className="bi bi-cash-stack" /> Montant Transaction (MAD)</label>
                    <input type="number" placeholder="Ex: 12500" value={platformForm.montant_transaction}
                      onChange={(e) => setPlatformForm({ ...platformForm, montant_transaction: e.target.value })} disabled={loading} />
                  </div>
                  <div className="td-field-row">
                    <div className="td-field">
                      <label><i className="bi bi-graph-up-arrow" /> Bénéfice Magasin (MAD)</label>
                      <input type="number" placeholder="Ex: 10000" value={platformForm.benefice_magasin}
                        onChange={(e) => setPlatformForm({ ...platformForm, benefice_magasin: e.target.value })} disabled={loading} />
                    </div>
                    <div className="td-field">
                      <label><i className="bi bi-person-badge" /> Bénéfice Commercial (MAD)</label>
                      <input type="number" placeholder="Ex: 1500" value={platformForm.benefice_commercial}
                        onChange={(e) => setPlatformForm({ ...platformForm, benefice_commercial: e.target.value })} disabled={loading} />
                    </div>
                  </div>
                  <div className="td-field">
                    <label><i className="bi bi-diagram-3" /> Bénéfice Plateforme (MAD)</label>
                    <input type="number" placeholder="Ex: 2500" value={platformForm.benefice_platform}
                      onChange={(e) => setPlatformForm({ ...platformForm, benefice_platform: e.target.value })} disabled={loading} />
                  </div>
                </>
              ) : (
                <>
                  <div className="td-field-row">
                    <div className="td-field">
                      <label><i className="bi bi-shop" /> Magasin Vendeur</label>
                      <input type="text" placeholder="Ex: Magasin Atlas" value={colisForm.magasin_vendeur}
                        onChange={(e) => setColisForm({ ...colisForm, magasin_vendeur: e.target.value })} disabled={loading} />
                    </div>
                    <div className="td-field">
                      <label><i className="bi bi-person" /> Client</label>
                      <input type="text" placeholder="Ex: Ahmed Ben Ali" value={colisForm.client}
                        onChange={(e) => setColisForm({ ...colisForm, client: e.target.value })} disabled={loading} />
                    </div>
                  </div>
                  <div className="td-field-row">
                    <div className="td-field">
                      <label><i className="bi bi-cash-stack" /> Montant (MAD)</label>
                      <input type="number" placeholder="Ex: 500" value={colisForm.montant}
                        onChange={(e) => setColisForm({ ...colisForm, montant: e.target.value })} disabled={loading} />
                    </div>
                    <div className="td-field">
                      <label><i className="bi bi-geo-alt" /> Adresse</label>
                      <input type="text" placeholder="Ex: 123 Rue de la Paix" value={colisForm.adresse}
                        onChange={(e) => setColisForm({ ...colisForm, adresse: e.target.value })} disabled={loading} />
                    </div>
                  </div>
                  <div className="td-field-row">
                    <div className="td-field">
                      <label><i className="bi bi-shop-window" /> Société de livraison</label>
                      <input type="text" placeholder="Ex: Magasin Rif" value={colisForm.magasin_retrait}
                        onChange={(e) => setColisForm({ ...colisForm, magasin_retrait: e.target.value })} disabled={loading} />
                    </div>
                    <div className="td-field">
                      <label><i className="bi bi-truck" /> Frais Livraison (MAD)</label>
                      <input type="number" placeholder="Ex: 120" value={colisForm.frais_livraison}
                        onChange={(e) => setColisForm({ ...colisForm, frais_livraison: e.target.value })} disabled={loading} />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="td-modal-footer">
              <button className="td-btn-cancel" onClick={() => !loading && setShowModal(false)} disabled={loading}>
                Annuler
              </button>
              <button className="td-btn-submit" onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <><span className="td-spinner" /> En cours...</>
                ) : (
                  <><i className="bi bi-plus-lg" /> Ajouter</>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Transaction Detail Modal / Drawer ── */}
      {selectedTransaction && (
        <>
          <div className="td-overlay" onClick={() => setSelectedTransaction(null)} />
          <div className="td-modal td-modal--detail">
            <div className="td-modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <h3>Détail de la Transaction #{selectedTransaction.id}</h3>
                {selectedTransaction.transaction_platform?.scenario_type && (
                  <span className={`td-type-badge ${getScenarioBadge(selectedTransaction.transaction_platform.scenario_type).cls}`}>
                    {getScenarioBadge(selectedTransaction.transaction_platform.scenario_type).label}
                  </span>
                )}
                {(selectedTransaction.ref_commande || selectedTransaction.transaction_platform?.ref_commande || selectedTransaction.transaction_colis?.ref_commande) && (
                  <div className="td-modal-ref-track">
                    <span style={{ fontSize: "11px", color: "var(--c-text-muted)" }}>Code Commande :</span>
                    <RefBadge code={selectedTransaction.ref_commande || selectedTransaction.transaction_platform?.ref_commande || selectedTransaction.transaction_colis?.ref_commande} />
                  </div>
                )}
              </div>
              <button className="td-modal-close" onClick={() => setSelectedTransaction(null)}>
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="td-modal-body td-detail-body">
              {selectedTransaction.type === "platform" && selectedTransaction.transaction_platform ? (
                <>
                  <div className="td-detail-grid">
                    <div className="td-detail-item">
                      <span className="td-detail-label">Code Commande (Tracking)</span>
                      <span className="td-detail-val">
                        <RefBadge code={selectedTransaction.ref_commande || selectedTransaction.transaction_platform.ref_commande} />
                      </span>
                    </div>
                    <div className="td-detail-item">
                      <span className="td-detail-label">Date</span>
                      <span className="td-detail-val">{fmtDate(selectedTransaction.date)}</span>
                    </div>
                    <div className="td-detail-item">
                      <span className="td-detail-label">Statut</span>
                      <span className="td-badge td-badge-complété">{selectedTransaction.transaction_platform.statut || "Validé"}</span>
                    </div>
                    <div className="td-detail-item">
                      <span className="td-detail-label">Magasin Origine (Propriétaire)</span>
                      <span className="td-detail-val bold">{selectedTransaction.transaction_platform.magasin_vendeur}</span>
                    </div>
                    <div className="td-detail-item">
                      <span className="td-detail-label">Magasin Acheteur / Client</span>
                      <span className="td-detail-val bold">{selectedTransaction.transaction_platform.magasin_acheteur}</span>
                    </div>
                    <div className="td-detail-item">
                      <span className="td-detail-label">Intermédiaire</span>
                      <span className="td-detail-val">
                        {selectedTransaction.transaction_platform.has_intermediaire
                          ? selectedTransaction.transaction_platform.intermediaire_name || selectedTransaction.transaction_platform.magasin_acheteur
                          : "Aucun (Vente directe)"}
                      </span>
                    </div>
                  </div>

                  <hr className="td-detail-divider" />
                  <h4 className="td-detail-title">Répartition Financière (Comptabilité)</h4>

                  <table className="td-detail-table">
                    <tbody>
                      <tr>
                        <td>Bénéfice Magasin Origine (Prix Original)</td>
                        <td className="num bold td-magasin-val">
                          {fmt(safeParseFloat(selectedTransaction.transaction_platform.benefice_magasin_originale ?? selectedTransaction.transaction_platform.benefice_magasin))}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          Bénéfice Intermédiaire
                          {selectedTransaction.transaction_platform.has_intermediaire ? "" : " (0 DH — pas d'intermédiaire)"}
                        </td>
                        <td className={`num bold ${selectedTransaction.transaction_platform.has_intermediaire ? "td-intermediaire-val" : "td-na-val"}`}>
                          {selectedTransaction.transaction_platform.has_intermediaire
                            ? fmt(safeParseFloat(selectedTransaction.transaction_platform.benefice_intermediaire))
                            : "0.00 MAD"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          Bénéfice WYN (Commission Plateforme)
                          {selectedTransaction.transaction_platform.commission_wyn_rate ? ` (${selectedTransaction.transaction_platform.commission_wyn_rate}%)` : ""}
                        </td>
                        <td className="num bold td-platform-val">
                          {fmt(safeParseFloat(selectedTransaction.transaction_platform.benefice_wyn ?? selectedTransaction.transaction_platform.benefice_platform))}
                        </td>
                      </tr>
                      {safeParseFloat(selectedTransaction.transaction_platform.total_remise) > 0 && (
                        <tr>
                          <td>Remise appliquée (-)</td>
                          <td className="num bold text-danger">
                            - {fmt(safeParseFloat(selectedTransaction.transaction_platform.total_remise))}
                          </td>
                        </tr>
                      )}
                      <tr className="td-detail-total-row">
                        <td className="bold">Montant Total Payé</td>
                        <td className="num bold" style={{ fontSize: "15px" }}>
                          {fmt(safeParseFloat(selectedTransaction.transaction_platform.montant_transaction))}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="td-detail-formula-box">
                    <i className="bi bi-info-circle-fill" />
                    <span>
                      {selectedTransaction.transaction_platform.has_intermediaire
                        ? "MontantTotal = BeneficeMagasinOrig + BeneficeIntermediaire + BeneficeWYN - Remise"
                        : "MontantTotal = BeneficeMagasinOrig + BeneficeWYN - Remise"}
                    </span>
                  </div>
                </>
              ) : (
                <div className="td-detail-grid">
                  <div className="td-detail-item">
                    <span className="td-detail-label">Code Commande (Tracking)</span>
                    <span className="td-detail-val">
                      <RefBadge code={selectedTransaction.ref_commande || selectedTransaction.transaction_colis?.ref_commande} />
                    </span>
                  </div>
                  <div className="td-detail-item">
                    <span className="td-detail-label">ID Colis</span>
                    <span className="td-detail-val bold">{selectedTransaction.transaction_colis?.id}</span>
                  </div>
                  <div className="td-detail-item">
                    <span className="td-detail-label">Client</span>
                    <span className="td-detail-val">{selectedTransaction.transaction_colis?.client}</span>
                  </div>
                  <div className="td-detail-item">
                    <span className="td-detail-label">Magasin Vendeur</span>
                    <span className="td-detail-val">{selectedTransaction.transaction_colis?.magasin_vendeur}</span>
                  </div>
                  <div className="td-detail-item">
                    <span className="td-detail-label">Société / Retrait</span>
                    <span className="td-detail-val">{selectedTransaction.transaction_colis?.magasin_retrait || "—"}</span>
                  </div>
                  <div className="td-detail-item">
                    <span className="td-detail-label">Adresse</span>
                    <span className="td-detail-val">{selectedTransaction.transaction_colis?.adresse || "—"}</span>
                  </div>
                  <div className="td-detail-item">
                    <span className="td-detail-label">Montant Colis</span>
                    <span className="td-detail-val num bold">{fmt(parseFloat(selectedTransaction.transaction_colis?.montant || "0"))}</span>
                  </div>
                  <div className="td-detail-item">
                    <span className="td-detail-label">Frais Livraison</span>
                    <span className="td-detail-val num bold">{fmt(parseFloat(selectedTransaction.transaction_colis?.frais_livraison || "0"))}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="td-modal-footer">
              <button className="td-btn-cancel" onClick={() => setSelectedTransaction(null)}>
                Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
}
